import { CartAddEvent, ThemeEvents } from '@theme/events';
import { readSampleCart } from '@theme/haus-sample-promotion';

class HausSampleBuilder extends HTMLElement {
  constructor() {
    super();
    this.quantities = new Map();
    this.cartQuantities = new Map();
    this.cards = [];
    this.cardByVariant = new Map();
    this.abortController = new AbortController();
    this.isSubmitting = false;
    this.selectedOnly = false;
    this.serverSavings = 0;
    this.serverSampleUnits = 0;
    this.serverNormalSampleUnits = 0;
    this.serverHasWelcome = false;
    this.serverPromotion = null;
    this.serverWelcomeUnits = 0;
    this.serverWelcomeCapacity = 0;
    this.serverEligibilityVerified = true;
    this.serverProjectionVerified = true;
    this.serverHasGift = false;
    this.cartReady = false;
    this.cartSyncSequence = 0;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.maxSamples = Number.parseInt(this.dataset.maxSamples || '20', 10);
    this.maxPerVariant = Number.parseInt(this.dataset.maxPerVariant || '5', 10);
    this.maximumMessage = (this.dataset.maximumMessage || '').replace('[max]', String(this.maxSamples));
    this.storageKey = this.dataset.storageKey || 'haus-sample-builder:v1';
    this.currency = this.dataset.currency || 'USD';
    this.isTradeCustomer = this.dataset.isTradeCustomer === 'true';
    this.verifiedDiscountProgress = !this.isTradeCustomer && this.dataset.enableVerifiedDiscountProgress === 'true';
    this.giftVariantId = this.dataset.giftVariantId || '';
    this.giftThreshold = Number.parseInt(this.dataset.giftThreshold || '15', 10);
    this.grid = this.querySelector('[data-sample-grid]');
    this.searchInput = this.querySelector('[data-sample-search]');
    this.filterSelect = this.querySelector('[data-sample-filter]');
    this.selectedFilterButton = this.querySelector('[data-selected-filter]');
    this.resetButton = this.querySelector('[data-filter-reset]');
    this.submitButton = this.querySelector('[data-builder-submit]');
    this.statusElement = this.querySelector('[data-builder-status]');
    this.confirmationElement = this.querySelector('[data-incoming-confirmation]');
    this.confirmationTextElement = this.querySelector('[data-incoming-confirmation-text]');
    this.selectedUnitsElement = this.querySelector('[data-selected-units]');
    this.visibleCountElement = this.querySelector('[data-visible-count]');
    this.summaryCountElement = this.querySelector('[data-summary-count]');
    this.summaryElement = this.querySelector('.haus-sample-builder__summary');
    this.subtotalElement = this.querySelector('[data-estimated-subtotal]');
    this.progressKickerElement = this.querySelector('[data-progress-kicker]');
    this.progressElement = this.querySelector('[data-progress-message]');
    this.progressPrimaryElement = this.querySelector('[data-progress-primary]');
    this.progressSecondaryElement = this.querySelector('[data-progress-secondary]');
    this.progressFillElements = [...this.querySelectorAll('[data-progress-fill]')];
    this.progressMarkers = [...this.querySelectorAll('[data-progress-marker]')];
    this.rewardMarkers = [...this.querySelectorAll('[data-reward-marker]')];
    this.savingsElement = this.querySelector('[data-confirmed-savings]');
    this.savingsValueElement = this.querySelector('[data-confirmed-savings-value]');
    this.savingsGiftElement = this.querySelector('[data-confirmed-gift]');
    this.noResultsElement = this.querySelector('[data-no-results]');

    this.cards = [...this.querySelectorAll('[data-sample-card]')].map((element) => {
      const card = {
        element,
        variantId: element.dataset.variantId || '',
        title: element.dataset.title || '',
        price: Number.parseInt(element.dataset.priceCents || '0', 10),
        materials: (element.dataset.materials || '')
          .split('|')
          .map((material) => material.trim())
          .filter(Boolean),
        search: element.dataset.search || '',
        available: element.dataset.available === 'true',
        maxPerFinish: Number.parseInt(element.dataset.maxPerFinish || String(this.maxPerVariant), 10),
        rewardEligible: element.dataset.rewardEligible !== 'false',
        decreaseButton: element.querySelector('[data-quantity-decrease]'),
        increaseButton: element.querySelector('[data-quantity-increase]'),
        quantityElement: element.querySelector('[data-card-quantity]'),
        limitElement: element.querySelector('[data-limit-message]'),
        detailsPanel: element.querySelector('[data-card-details]'),
        frontPanel: element.querySelector('[data-card-front]'),
        detailsButtons: [...element.querySelectorAll('[data-details-toggle]')],
      };
      this.cardByVariant.set(card.variantId, card);
      return card;
    });

    this.cardByVariant = new Map(this.cards.map((card) => [card.variantId, card]));

    if (!this.cards.length) return;

    this.populateCategories();
    this.bindEvents();
    this.updateControlAvailability();
    this.applyFilters();
    this.setupChatAvoidance();
    this.initializeCartState();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.chatResizeObserver?.disconnect();
    this.chatMutationObserver?.disconnect();
  }

  async initializeCartState() {
    await this.syncCartState({ render: false });
    this.cartReady = true;
    this.restoreSelection();
    this.renderAll();
    this.applyFilters();
  }

  populateCategories() {
    if (!this.filterSelect) return;

    const categories = [...new Set(this.cards.flatMap((card) => card.materials))].sort((a, b) => a.localeCompare(b));

    for (const category of categories) {
      const option = document.createElement('option');
      option.value = category;
      option.textContent = category.charAt(0).toUpperCase() + category.slice(1);
      this.filterSelect.append(option);
    }
  }

  restoreSelection() {
    const storedQuantities = this.readStoredSelection();

    for (const [variantId, storedQuantity] of storedQuantities) {
      const card = this.cardByVariant.get(String(variantId));
      if (!card?.available || this.totalUnits() >= this.maxSamples) continue;
      const availableCapacity = this.maxSamples - this.totalUnits();
      const perFinishCapacity = this.selectionLimitForCard(card);
      const quantity = Math.min(
        Math.max(Number.parseInt(storedQuantity, 10) || 0, 0),
        availableCapacity,
        perFinishCapacity
      );
      if (quantity > 0) this.quantities.set(card.variantId, quantity);
    }

    const incomingId = new URLSearchParams(window.location.search).get('sample');
    const incomingCard = incomingId && /^\d+$/.test(incomingId) ? this.cardByVariant.get(incomingId) : null;

    if (incomingId) {
      if (!incomingCard) {
        this.setStatus(this.dataset.invalidMessage);
      } else if (!incomingCard.available) {
        this.setStatus(this.dataset.unavailableMessage);
      } else {
        this.quantities.clear();
        if (this.selectionLimitForCard(incomingCard) > 0) this.quantities.set(incomingCard.variantId, 1);

        if ((this.quantities.get(incomingCard.variantId) || 0) > 0) {
          if (this.confirmationTextElement) {
            this.confirmationTextElement.textContent = (this.dataset.confirmationMessage || '').replace(
              '[product]',
              incomingCard.title
            );
          }
          if (this.confirmationElement) this.confirmationElement.hidden = false;
          if (new URLSearchParams(window.location.search).get('focus') === 'details') {
            window.requestAnimationFrame(() => {
              incomingCard.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              window.setTimeout(() => this.openDetails(incomingCard, incomingCard.detailsButtons[0]), 360);
            });
          }
        } else {
          this.setStatus(`${incomingCard.title} is already at the ${this.maxPerVariant}-sample limit.`);
        }
      }
    }

    this.saveSelection();
  }

  cartQuantityForCard(card) {
    return this.cartQuantities.get(card.variantId) || 0;
  }

  selectionLimitForCard(card) {
    const configuredLimit = card.maxPerFinish > 0 ? card.maxPerFinish : this.maxPerVariant;
    return Math.max(Math.min(configuredLimit, this.maxPerVariant - this.cartQuantityForCard(card)), 0);
  }

  clampSelectionsToCart() {
    let remainingCapacity = this.maxSamples;
    let changed = false;

    for (const [variantId, storedQuantity] of [...this.quantities]) {
      const card = this.cardByVariant.get(variantId);
      if (!card?.available) {
        this.quantities.delete(variantId);
        changed = true;
        continue;
      }

      const quantity = Math.min(
        Math.max(Number.parseInt(storedQuantity, 10) || 0, 0),
        this.selectionLimitForCard(card),
        remainingCapacity
      );
      if (quantity !== storedQuantity) changed = true;
      if (quantity > 0) {
        this.quantities.set(variantId, quantity);
        remainingCapacity -= quantity;
      } else {
        this.quantities.delete(variantId);
      }
    }

    return changed;
  }

  readStoredSelection() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '{}');
      if (Array.isArray(stored)) return new Map(stored.map((variantId) => [String(variantId), 1]));
      if (stored && typeof stored === 'object') return new Map(Object.entries(stored));
      return new Map();
    } catch {
      return new Map();
    }
  }

  saveSelection() {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(Object.fromEntries(this.quantities)));
    } catch {
      return;
    }
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.grid?.addEventListener('click', (event) => this.handleGridClick(event), { signal });
    this.searchInput?.addEventListener('input', () => this.applyFilters(), { signal });
    this.filterSelect?.addEventListener('change', () => this.applyFilters(), { signal });
    this.selectedFilterButton?.addEventListener(
      'click',
      () => {
        this.selectedOnly = !this.selectedOnly;
        this.selectedFilterButton.setAttribute('aria-pressed', String(this.selectedOnly));
        this.applyFilters();
      },
      { signal }
    );
    this.resetButton?.addEventListener('click', () => this.resetFilters(), { signal });
    this.submitButton?.addEventListener('click', () => this.addSelectedToCart(), { signal });
    for (const marker of this.rewardMarkers) {
      marker.addEventListener('focus', () => {
        marker.classList.remove('is-tooltip-dismissed');
        marker.setAttribute('aria-expanded', 'true');
      }, { signal });
      marker.addEventListener('blur', () => {
        marker.classList.remove('is-tooltip-dismissed');
        if (!marker.classList.contains('is-tooltip-open')) marker.setAttribute('aria-expanded', 'false');
      }, { signal });
      marker.addEventListener('click', (event) => {
        event.stopPropagation();
        marker.classList.remove('is-tooltip-dismissed');
        this.toggleRewardTooltip(marker);
      }, { signal });
    }
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-reward-marker]')) {
        this.closeRewardTooltips();
      }
    }, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, () => this.syncCartState(), { signal });
    document.addEventListener(ThemeEvents.discountUpdate, () => this.syncCartState(), { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.syncCartState();
    }, { signal });
    window.addEventListener(
      'pageshow',
      () => {
        this.syncCartState();
        this.updateChatSafeArea();
      },
      { signal }
    );
    window.addEventListener('resize', () => this.updateChatSafeArea(), { signal });
  }

  handleGridClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const cardElement = target.closest('[data-sample-card]');
    if (!cardElement) return;
    const card = this.cardByVariant.get(cardElement.dataset.variantId || '');
    if (!card) return;

    if (target.closest('[data-quantity-increase]')) {
      this.changeQuantity(card, 1);
    } else if (target.closest('[data-quantity-decrease]')) {
      this.changeQuantity(card, -1);
    } else if (target.closest('[data-card-details]')) {
      if (target.closest('a, button, input, select, textarea, summary, [role="button"]')) return;
      this.closeDetails(card);
    } else if (target.closest('[data-details-toggle]')) {
      this.openDetails(card, target.closest('[data-details-toggle]'));
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    const openRewardMarker = this.rewardMarkers.find((marker) => marker.classList.contains('is-tooltip-open'));
    if (openRewardMarker) {
      this.closeRewardTooltips();
      openRewardMarker.classList.add('is-tooltip-dismissed');
      openRewardMarker.focus({ preventScroll: true });
      return;
    }
    const openCard = this.cards.find((card) => card.element.classList.contains('is-details-open'));
    if (openCard) this.closeDetails(openCard);
  }

  toggleRewardTooltip(marker) {
    const willOpen = !marker.classList.contains('is-tooltip-open');
    this.closeRewardTooltips();
    marker.classList.toggle('is-tooltip-open', willOpen);
    marker.setAttribute('aria-expanded', String(willOpen));
  }

  closeRewardTooltips() {
    for (const marker of this.rewardMarkers) {
      marker.classList.remove('is-tooltip-open');
      marker.setAttribute('aria-expanded', 'false');
    }
  }

  openDetails(card, opener) {
    for (const otherCard of this.cards) {
      if (otherCard !== card && otherCard.element.classList.contains('is-details-open')) this.closeDetails(otherCard);
    }

    card.lastDetailsOpener = opener;
    card.element.classList.add('is-details-open');
    card.frontPanel?.setAttribute('aria-hidden', 'true');
    card.detailsPanel?.setAttribute('aria-hidden', 'false');
    if (card.frontPanel) card.frontPanel.inert = true;
    if (card.detailsPanel) card.detailsPanel.inert = false;
    for (const button of card.detailsButtons) button.setAttribute('aria-expanded', 'true');
    card.detailsPanel?.focus({ preventScroll: true });
  }

  closeDetails(card) {
    card.element.classList.remove('is-details-open');
    card.frontPanel?.setAttribute('aria-hidden', 'false');
    card.detailsPanel?.setAttribute('aria-hidden', 'true');
    if (card.frontPanel) card.frontPanel.inert = false;
    if (card.detailsPanel) card.detailsPanel.inert = true;
    for (const button of card.detailsButtons) button.setAttribute('aria-expanded', 'false');
    card.lastDetailsOpener?.focus({ preventScroll: true });
  }

  changeQuantity(card, delta) {
    if (!card.available || !this.cartReady || this.isSubmitting) return;
    const currentQuantity = this.quantities.get(card.variantId) || 0;

    if (delta > 0 && this.totalUnits() >= this.maxSamples) {
      this.setStatus(this.maximumMessage);
      return;
    }

    const selectionLimit = this.selectionLimitForCard(card);
    if (delta > 0 && currentQuantity >= selectionLimit) {
      this.setStatus(`${card.title} is at the ${this.maxPerVariant}-sample limit including your cart.`);
      return;
    }

    const nextQuantity = Math.min(Math.max(0, currentQuantity + delta), selectionLimit);
    if (nextQuantity === 0) {
      this.quantities.delete(card.variantId);
    } else {
      this.quantities.set(card.variantId, nextQuantity);
    }

    const currentTotal = this.totalUnits();
    this.setStatus(currentTotal >= this.maxSamples ? this.maximumMessage : '');
    this.saveSelection();
    this.updateControlAvailability();
    this.updateCard(card);
    this.renderSummary();
    if (this.selectedOnly) this.applyFilters();
  }

  totalUnits() {
    let total = 0;
    for (const quantity of this.quantities.values()) total += quantity;
    return total;
  }

  rewardEligibleUnits() {
    let total = 0;
    for (const [variantId, quantity] of this.quantities) {
      if (this.cardByVariant.get(variantId)?.rewardEligible) total += quantity;
    }
    return total;
  }

  projectedQualifyingCents() {
    const p = this.serverPromotion;
    let selectedValue = 0;
    for (const [id, quantity] of this.quantities) {
      const card = this.cardByVariant.get(id);
      if (card?.rewardEligible) selectedValue += card.price * quantity;
    }
    return Math.max(0, (p?.qualifyingValueCents || 0) + selectedValue);
  }

  projectedRewardEligibleUnits() {
    return this.projectedQualifyingCents() / 500;
  }

  renderAll() {
    for (const card of this.cards) this.updateCard(card);
    this.updateControlAvailability();
    this.renderSummary();
  }

  updateCard(card) {
    const quantity = this.quantities.get(card.variantId) || 0;
    const cartQuantity = this.cartQuantityForCard(card);
    const atVariantMaximum = cartQuantity + quantity >= this.maxPerVariant;
    card.element.classList.toggle('is-selected', quantity > 0);
    card.element.classList.toggle('is-at-limit', atVariantMaximum);
    card.element.dataset.quantity = String(quantity);
    if (card.quantityElement) card.quantityElement.textContent = String(quantity);
    if (card.limitElement) {
      card.limitElement.hidden = !atVariantMaximum;
      card.limitElement.textContent = cartQuantity >= this.maxPerVariant && quantity === 0 ? '5 IN CART' : 'MAX 5';
    }
    if (card.decreaseButton) card.decreaseButton.disabled = !card.available || quantity === 0 || this.isSubmitting;
  }

  updateControlAvailability() {
    const atMaximum = this.totalUnits() >= this.maxSamples;
    for (const card of this.cards) {
      const atFinishMaximum = (this.quantities.get(card.variantId) || 0) >= this.selectionLimitForCard(card);
      if (card.increaseButton) {
        card.increaseButton.disabled =
          !card.available || !this.cartReady || atMaximum || atFinishMaximum || this.isSubmitting;
      }
      if (card.decreaseButton) {
        card.decreaseButton.disabled =
          !card.available || (this.quantities.get(card.variantId) || 0) === 0 || this.isSubmitting;
      }
    }
  }

  applyFilters() {
    const query = this.searchInput?.value.trim().toLowerCase() || '';
    const category = this.filterSelect?.value || '';
    let visibleCount = 0;

    for (const card of this.cards) {
      const matchesSearch = !query || card.search.includes(query);
      const matchesCategory = !category || card.materials.includes(category);
      const matchesSelected = !this.selectedOnly || this.quantities.has(card.variantId);
      card.element.hidden = !(matchesSearch && matchesCategory && matchesSelected);
      if (!card.element.hidden) visibleCount += 1;
    }

    if (this.visibleCountElement) this.visibleCountElement.textContent = String(visibleCount);
    if (this.noResultsElement) this.noResultsElement.hidden = visibleCount > 0;
    if (this.resetButton) this.resetButton.disabled = !query && !category && !this.selectedOnly;
  }

  resetFilters() {
    if (this.searchInput) this.searchInput.value = '';
    if (this.filterSelect) this.filterSelect.value = '';
    this.selectedOnly = false;
    this.selectedFilterButton?.setAttribute('aria-pressed', 'false');
    this.applyFilters();
  }

  renderSummary() {
    const units = this.totalUnits();
    const finishes = this.quantities.size;
    let subtotal = 0;

    for (const [variantId, quantity] of this.quantities) {
      const card = this.cardByVariant.get(variantId);
      if (card) subtotal += card.price * quantity;
    }

    if (this.selectedUnitsElement) this.selectedUnitsElement.textContent = String(units);
    if (this.summaryCountElement) {
      const sampleLabel = units === 1 ? 'sample' : 'samples';
      const finishLabel = finishes === 1 ? 'finish' : 'finishes';
      this.summaryCountElement.textContent = units
        ? `${units} ${sampleLabel} across ${finishes} ${finishLabel}, selected but not yet in cart`
        : `0 samples across 0 finishes`;
    }
    if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(subtotal);
    if (this.submitButton) this.submitButton.disabled = units === 0 || this.isSubmitting;
    if (this.selectedFilterButton) {
      this.selectedFilterButton.textContent = finishes > 0 ? `Selected (${finishes})` : 'Selected';
    }

    const progressUnits = this.verifiedDiscountProgress ? (this.serverPromotion?.projectionVerified === false ? this.serverSampleUnits : this.projectedRewardEligibleUnits()) : units;
    this.renderProgress(progressUnits);
    this.renderCreditProgress();
    this.renderServerSavings();
  }

  renderProgress(units) {
    const progressMaximum = this.verifiedDiscountProgress ? this.giftThreshold : this.maxSamples;
    const percentage = Math.min((units / progressMaximum) * 100, 100);
    for (const fill of this.progressFillElements) fill.style.width = `${percentage}%`;

    for (const marker of this.progressMarkers) {
      const threshold = Number.parseInt(marker.dataset.progressMarker || '0', 10);
      marker.classList.toggle('is-reached', units >= threshold);
      marker.classList.toggle('is-verified', this.verifiedDiscountProgress);
    }

    if (this.verifiedDiscountProgress) {
      this.renderVerifiedProgress(units);
      return;
    }

    const remaining = Math.max(this.maxSamples - units, 0);
    if (this.progressKickerElement) this.progressKickerElement.textContent = 'Building your box';
    if (units === 0) {
      this.setProgressMessage('Choose a sample to start your box.');
    } else if (remaining > 0) {
      this.setProgressMessage(
        `Add ${remaining} more ${remaining === 1 ? 'sample' : 'samples'} to complete your box.`
      );
    } else {
      this.setProgressMessage(`Your ${this.maxSamples}-sample box is complete.`);
    }
  }

  renderCreditProgress() {
    const panel = this.querySelector('[data-credit-progress]');
    if (!panel) return;
    const p = this.serverPromotion;
    panel.hidden = false;
    if (!p?.hasCredit) {
      const message = this.querySelector('[data-credit-message]');
      if (message) message.textContent = 'No sample credit applied. Credit-funded samples are separate from bundle rewards.';
      const fill = this.querySelector('[data-credit-fill]');
      if (fill) fill.style.width = '0%';
      const track = this.querySelector('[data-credit-track]');
      if (track) {track.setAttribute('aria-valuenow','0');track.setAttribute('aria-valuetext','No sample credit applied');}
      return;
    }
    const label = this.querySelector('[data-credit-label]');
    const text = this.querySelector('[data-credit-message]');
    const fill = this.querySelector('[data-credit-fill]');
    const track = this.querySelector('[data-credit-track]');
    if (label) label.textContent = p.hasWelcome ? 'Welcome sample credit' : 'Sample credit';
    if (text) text.textContent = p.eligibilityVerified
      ? `${this.formatRewardMoney(p.creditCents)} applied to samples. Credit does not earn bundle rewards.`
      : 'Sample credit applied. The exact credit and reward split will be confirmed as your cart updates.';
    const maximum = p.hasWelcome ? 2500 : Math.max(p.normalValueCents, 1);
    const percentage = Math.min(100, p.creditCents / maximum * 100);
    if (fill) fill.style.width = `${percentage}%`;
    if (track) {track.setAttribute('aria-valuenow', String(p.creditCents));track.setAttribute('aria-valuemax',String(maximum));track.setAttribute('aria-valuetext', `${this.formatRewardMoney(p.creditCents)} applied`);}
  }

  renderVerifiedProgress(units) {
    const p = this.serverPromotion;
    if (p?.hasCredit && (!p.eligibilityVerified || (!p.projectionVerified && this.rewardEligibleUnits() > 0))) {
      this.setProgressMessage('Add these samples to confirm your bundle rewards.', 'Credit-funded value does not fill the bundle bar.');
      return;
    }
    const cents = Math.round(units * 500);
    let message;
    if (cents < 2500) message = `Add ${this.formatRewardMoney(2500-cents)} to unlock 1 free sample`;
    else if (cents < 5000) message = `Add ${this.formatRewardMoney(5000-cents)} to unlock 3 free samples`;
    else if (cents < 7500) message = `Add ${this.formatRewardMoney(7500-cents)} to unlock your free grout sampler kit`;
    else message = '3 free samples + free grout sampler kit unlocked';
    this.setProgressMessage(message, `${this.formatRewardMoney(cents)} qualifying sample value, before bundle savings${this.totalUnits() ? ' (includes your selection)' : ''}.`);
  }

  setProgressMessage(primary, secondary = '') {
    if (this.progressPrimaryElement) {
      this.progressPrimaryElement.textContent = primary;
      if (this.progressSecondaryElement) {
        this.progressSecondaryElement.textContent = secondary;
        this.progressSecondaryElement.hidden = !secondary;
      }
      return;
    }

    if (this.progressElement) this.progressElement.textContent = secondary ? `${primary} ${secondary}` : primary;
  }
rewardFreeUnits(units) {
    if (!this.verifiedDiscountProgress || this.isTradeCustomer) return 0;
    if (units >= 10) return 3;
    if (units >= 5) return 1;
    return 0;
  }

  projectedSelectionSavings() {
    if (!this.verifiedDiscountProgress || this.isTradeCustomer || this.serverPromotion?.projectionVerified === false) return 0;

    const selectedUnits = this.rewardEligibleUnits();
    if (!selectedUnits) return 0;

    const currentFreeUnits = this.rewardFreeUnits(this.serverSampleUnits);
    const projectedFreeUnits = this.rewardFreeUnits(this.projectedRewardEligibleUnits());
    const newlyFreeUnits = Math.max(projectedFreeUnits - currentFreeUnits, 0);
    if (!newlyFreeUnits) return 0;

    const eligiblePrices = [];
    for (const [variantId, quantity] of this.quantities) {
      const card = this.cardByVariant.get(variantId);
      if (!card?.rewardEligible || card.price <= 0) continue;
      for (let index = 0; index < quantity; index += 1) eligiblePrices.push(card.price);
    }

    if (!eligiblePrices.length) return 0;
    eligiblePrices.sort((a, b) => a - b);

    const fallbackPrice = eligiblePrices[0];
    let savings = 0;
    for (let index = 0; index < newlyFreeUnits; index += 1) {
      savings += eligiblePrices[index] ?? fallbackPrice;
    }

    return savings;
  }
  renderServerSavings() {
    if (this.isTradeCustomer) {
      if (this.savingsElement) this.savingsElement.hidden = true;
      return;
    }

    const projectedSavings = this.projectedSelectionSavings();
    const hasProjectedSavings = projectedSavings > 0;
    const savings = hasProjectedSavings ? projectedSavings : this.serverSavings;
    const projectedUnits = this.projectedRewardEligibleUnits();
    const hasGift =
      this.serverHasGift ||
      (this.verifiedDiscountProgress && this.serverPromotion?.projectionVerified !== false && projectedUnits >= this.giftThreshold);
    const showReward = savings > 0 || hasGift;

    if (this.savingsElement) this.savingsElement.hidden = !showReward;
    if (this.savingsLabelElement) {
      this.savingsLabelElement.textContent = hasProjectedSavings ? 'Estimated savings' : 'Sample reward';
    }
    if (this.savingsValueElement) {
      this.savingsValueElement.hidden = savings <= 0;
      this.savingsValueElement.textContent = savings > 0 ? `${this.formatRewardMoney(savings)} saved` : '';
    }
    if (this.savingsGiftElement) this.savingsGiftElement.hidden = !hasGift;
  }

  sampleRewardSavings(cart) {
    if (this.isTradeCustomer) return 0;
    if (!Array.isArray(cart?.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (!Array.isArray(item.line_level_discount_allocations)) return total;
      return (
        total +
        item.line_level_discount_allocations.reduce((lineTotal, allocation) => {
          const title = allocation?.discount_application?.title || '';
          const isSampleReward = title.startsWith('HAUS Samples') || title.startsWith('Sample reward');
          return isSampleReward ? lineTotal + (Number.parseInt(allocation.amount || '0', 10) || 0) : lineTotal;
        }, 0)
      );
    }, 0);
  }

  async syncCartState({ render = true } = {}) {
    const syncSequence = ++this.cartSyncSequence;
    try {
      const snapshot = await readSampleCart();
      const { cart, promotion } = snapshot;
      if (syncSequence !== this.cartSyncSequence) return null;

      this.cartQuantities = new Map();
      if (Array.isArray(cart.items)) {
        for (const item of cart.items) {
          const variantId = String(item.variant_id || item.id || '');
          if (!this.cardByVariant.has(variantId)) continue;
          this.cartQuantities.set(variantId, (this.cartQuantities.get(variantId) || 0) + (item.quantity || 0));
        }
      }

      this.serverPromotion = promotion;
      this.serverSavings = this.isTradeCustomer ? 0 : promotion.earnedDiscountCents;
      this.serverSampleUnits = promotion.eligibleQuantity;
      this.serverNormalSampleUnits = promotion.normalQuantity;
      this.serverHasWelcome = promotion.hasWelcome;
      this.serverWelcomeUnits = promotion.welcomeQuantity;
      this.serverWelcomeCapacity = promotion.welcomeCapacity;
      this.serverEligibilityVerified = promotion.eligibilityVerified;
      this.serverProjectionVerified = promotion.projectionVerified;

      this.serverHasGift = promotion.giftQuantity === 1 && Array.isArray(cart.items)
        ? cart.items.some(
            (item) => String(item.variant_id || item.id) === this.giftVariantId && Boolean(item.properties?._haus_sample_reward)
          )
        : false;
      const selectionChanged = this.clampSelectionsToCart();
      if (selectionChanged) this.saveSelection();
      if (render) this.renderAll();
      return { cart, selectionChanged };
    } catch {
      return null;
    }
  }

  setupChatAvoidance() {
    const refresh = () => this.updateChatSafeArea();
    window.requestAnimationFrame(refresh);
    window.addEventListener('load', refresh, { signal: this.abortController.signal });
    window.setTimeout(refresh, 300);
    window.setTimeout(refresh, 1200);
  }

  updateChatSafeArea() {
    if (!this.summaryElement) return;

    const chatHost = document.querySelector('shopify-chat');
    const chatActivator = chatHost?.shadowRoot?.querySelector('[part="activator"]');
    if (!(chatActivator instanceof HTMLElement)) {
      this.summaryElement.style.removeProperty('--haus-chat-safe-bottom');
      this.summaryElement.style.removeProperty('--haus-chat-safe-inline');
      return;
    }

    if (this.chatActivator !== chatActivator) {
      this.chatActivator = chatActivator;
      this.chatResizeObserver?.disconnect();
      this.chatMutationObserver?.disconnect();
      this.chatResizeObserver = new ResizeObserver(() => this.updateChatSafeArea());
      this.chatResizeObserver.observe(chatActivator);
      this.chatMutationObserver = new MutationObserver(() => this.updateChatSafeArea());
      this.chatMutationObserver.observe(chatHost.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    const rect = chatActivator.getBoundingClientRect();
    const isVisible = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    if (!isVisible) {
      this.summaryElement.style.removeProperty('--haus-chat-safe-bottom');
      this.summaryElement.style.removeProperty('--haus-chat-safe-inline');
      return;
    }

    if (window.matchMedia('(max-width: 989px)').matches) {
      const safeBottom = Math.max(window.innerHeight - rect.top + 8, 0);
      this.summaryElement.style.removeProperty('--haus-chat-safe-inline');
      this.summaryElement.style.setProperty('--haus-chat-safe-bottom', `${safeBottom}px`);
      return;
    }

    const parentRight = this.summaryElement.parentElement?.getBoundingClientRect().right || window.innerWidth;
    const safeInline = Math.max(parentRight - rect.left + 8, 0);
    this.summaryElement.style.removeProperty('--haus-chat-safe-bottom');
    this.summaryElement.style.setProperty('--haus-chat-safe-inline', `${safeInline}px`);
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  formatRewardMoney(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch {
      return `$${Math.round(cents / 100)}`;
    }
  }

  setStatus(message) {
    if (this.statusElement) this.statusElement.textContent = message || '';
  }

  async addSelectedToCart() {
    if (this.isSubmitting || !this.cartReady) return;
    if (!this.totalUnits()) {
      this.setStatus(this.dataset.emptySelectionMessage);
      return;
    }

    this.isSubmitting = true;
    this.setStatus('');
    this.setAttribute('aria-busy', 'true');
    this.updateControlAvailability();
    this.renderSummary();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const preflight = await this.syncCartState({ render: false });
      if (!preflight) throw new Error(this.dataset.errorMessage);
      if (preflight.selectionChanged) {
        this.setStatus('Your cart changed. Selections were adjusted to the 5-per-sample limit.');
        return;
      }

      const totalUnits = this.totalUnits();
      if (!totalUnits) {
        this.setStatus(this.dataset.emptySelectionMessage);
        return;
      }

      const items = [...this.quantities].map(([id, quantity]) => ({
        id: Number(id),
        quantity,
        properties: { _haus_sample_builder: 'true' },
      }));

      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        ),
      ].slice(0, 5);

      const requestBody = {
        items,
        sections_url: window.location.pathname,
      };
      if (sectionIds.length) requestBody.sections = sectionIds.join(',');

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error(this.dataset.errorMessage);
      }

      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || this.dataset.errorMessage);
      }

      let cart = {};
      const cartSections = result.sections;
      let itemCount = totalUnits;
      let eventSource = 'product-form-component';

      this.quantities.clear();
      this.saveSelection();
      let rewardError = null;
      try {
        await window.HausSampleRewardGuard?.reconcile();
      } catch (error) {
        rewardError = error;
      }
      const latestCartState = await this.syncCartState({ render: false });
      if (latestCartState) {
        cart = latestCartState.cart;
        itemCount = cart.item_count;
        eventSource = 'haus-sample-builder';
      }

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: eventSource,
          itemCount,
          sections: cartSections,
        })
      );

      this.quantities.clear();
      this.saveSelection();
      if (this.confirmationElement) this.confirmationElement.hidden = true;
      this.selectedOnly = false;
      this.selectedFilterButton?.setAttribute('aria-pressed', 'false');
      this.setStatus(`${totalUnits} ${totalUnits === 1 ? 'sample was' : 'samples were'} added. Your cart now has ${itemCount} ${itemCount === 1 ? 'item' : 'items'}.${rewardError ? ` ${rewardError.message}` : ''}`);
      this.applyFilters();
    } catch (error) {
      this.setStatus(error instanceof Error && error.message ? error.message : this.dataset.errorMessage);
    } finally {
      this.isSubmitting = false;
      this.removeAttribute('aria-busy');
      this.renderAll();
    }
  }
}

if (!customElements.get('haus-sample-builder')) {
  customElements.define('haus-sample-builder', HausSampleBuilder);
}

